import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
 Card,
 CardContent,
 CardHeader,
 CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
 Loader2,
 CheckCircle,
 Users,
 Calendar,
 ArrowLeft,
 Download,
 FileText,
} from "lucide-react";
import api from "../../api/client";

export default function AcceptedApplications() {
 const navigate = useNavigate();
 const [applications, setApplications] = useState([]);
 const [filteredApps, setFilteredApps] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState("");
 const [search, setSearch] = useState("");

 const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

 useEffect(() => {
 fetchAcceptedApplications();
 }, []);

 useEffect(() => {
 if (search) {
 const filtered = applications.filter((app) => {
 const profile = app.applier?.applierProfile;
 return (
 profile?.fullName?.toLowerCase().includes(search.toLowerCase()) ||
 app.applier?.email?.toLowerCase().includes(search.toLowerCase()) ||
 app.job?.title?.toLowerCase().includes(search.toLowerCase())
 );
 });
 setFilteredApps(filtered);
 } else {
 setFilteredApps(applications);
 }
 }, [search, applications]);

 const fetchAcceptedApplications = async () => {
 try {
 const res = await api.get("/recruiter/applications");
 if (!res.data || !Array.isArray(res.data.data)) {
 setError("Invalid data format received.");
 return;
 }
 const accepted = res.data.data.filter((app) => app.status === "ACCEPTED"); // Filter the unwrapped array
 setApplications(accepted);
 setFilteredApps(accepted);
 } catch (err) {
 console.error("Failed to load accepted applications:", err);
 setError("Failed to load accepted applications");
 } finally {
 setLoading(false);
 }
 };

 const downloadResume = async (url, applierName) => {
 try {
 const res = await fetch(url);
 if (!res.ok) throw new Error(`HTTP ${res.status}`);

 const blob = await res.blob();
 const link = document.createElement("a");
 const fileExtension = url.split(".").pop();
 const filename = `${applierName || "resume"}.${fileExtension}`;
 link.href = window.URL.createObjectURL(blob);
 link.download = filename;
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 } catch (err) {
 alert(`Failed to download resume. ${err.message}`);
 }
 };

 if (loading) {
 return (
 <div className="min-h-screen flex items-center justify-center ">
 
 <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
 </div>
 );
 }

 return (
 <div className="min-h-screen py-8">
 
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* Header */}
 <div className="mb-8">
 <Button
 variant="ghost"
 className="mb-4"
 onClick={() => navigate("/recruiter/dashboard")}
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Dashboard
 </Button>
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
 <div>
 <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
 <CheckCircle className="w-8 h-8 text-green-600" />
 Accepted Applications
 </h1>
 <p className="text-muted-foreground mt-2">
 View all candidates you've accepted across all job postings
 </p>
 </div>
 <Card className="border-green-200 dark:border-green-800 bg-green-500/10/50 ">
 <CardContent className="p-4">
 <div className="text-3xl font-bold text-green-700 dark:text-green-300">
 {filteredApps.length}
 </div>
 <div className="text-sm text-muted-foreground">
 Total Accepted
 </div>
 </CardContent>
 </Card>
 </div>
 </div>

 {/* Error Alert */}
 {error && (
 <Alert variant="destructive" className="mb-6">
 <AlertDescription>{error}</AlertDescription>
 </Alert>
 )}

 {/* Search */}
 <div className="mb-6">
 <Input
 placeholder="Search by name, email, or job title..."
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 className="max-w-md h-12 rounded-xl bg-card border-border"
 />
 </div>

 {/* Applications Grid */}
 {filteredApps.length === 0 ? (
 <Card>
 <CardContent className="text-center py-16">
 <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
 <p className="text-lg text-muted-foreground">
 {search
 ? "No accepted applications match your search"
 : "No accepted applications yet"}
 </p>
 </CardContent>
 </Card>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
 {filteredApps.map((app) => {
 const profile = app.applier?.applierProfile;
 const resumeUrl = profile?.resumeUrl || profile?.resume;

 return (
 <Card
 key={app.id}
 className="hover:shadow-xl transition-all duration-300 border-green-200 dark:border-green-800 bg-card/50"
 >
 <CardHeader className="pb-3">
 <div className="flex items-start justify-between">
 <div className="flex-1">
 <CardTitle className="text-lg font-bold text-foreground">
 {profile?.fullName || "N/A"}
 </CardTitle>
 <p className="text-sm text-muted-foreground mt-1">
 {app.applier?.email}
 </p>
 </div>
 <Badge className="bg-green-100 text-green-800 dark:text-green-300">
 <CheckCircle className="w-3 h-3 mr-1" />
 Accepted
 </Badge>
 </div>
 </CardHeader>

 <CardContent className="space-y-3">
 {/* Job Title */}
 <div>
 <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
 Job Position
 </p>
 <p
 className="text-sm font-medium text-primary cursor-pointer hover:underline"
 onClick={() =>
 navigate(`/recruiter/jobs/${app.jobId}/applicants`)
 }
 >
 {app.job?.title || "N/A"}
 </p>
 </div>

 {/* University & GPA */}
 <div className="grid grid-cols-2 gap-3">
 <div>
 <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
 University
 </p>
 <p className="text-sm font-medium text-foreground">
 {profile?.university || "N/A"}
 </p>
 </div>
 <div>
 <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
 GPA
 </p>
 <p className="text-sm font-medium text-foreground">
 {profile?.gpa || "N/A"}
 </p>
 </div>
 </div>

 {/* Applied Date */}
 <div>
 <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold flex items-center gap-1">
 <Calendar className="w-3 h-3" />
 Applied On
 </p>
 <p className="text-sm font-medium text-foreground">
 {new Date(app.appliedAt).toLocaleDateString("en-US", {
 month: "short",
 day: "numeric",
 year: "numeric",
 })}
 </p>
 </div>

 {/* Actions */}
 <div className="flex gap-2 pt-2">
 {resumeUrl && (
 <Button
 size="sm"
 variant="outline"
 onClick={() => {
 const url = resumeUrl.startsWith('http') 
 ? resumeUrl 
 : `${API_BASE_URL}${resumeUrl}`;
 window.open(url, '_blank');
 }}
 className="flex-1"
 >
 <Download className="w-4 h-4 mr-1" />
 Resume
 </Button>
 )}
 <Button
 size="sm"
 onClick={() =>
 navigate(`/recruiter/applications/${app.id}`)
 }
 className="flex-1"
 >
 <FileText className="w-4 h-4 mr-1" />
 View Details
 </Button>
 </div>
 </CardContent>
 </Card>
 );
 })}
 </div>
 )}
 </div>
 </div>
 );
}
